<template>
  <button
    ref="button"
    color="button"
    :disabled="disabled"
    :aria-disabled="disabled"
    class="group outline-secondary relative z-0 flex shrink-0 cursor-pointer! flex-row items-center justify-center overflow-hidden whitespace-nowrap outline-offset-2 transition-all focus-visible:outline-[3px] disabled:cursor-default!"
    :class="[buttonHeight, colorClasses, borderRadius, padding, label]"
  >
    <BaseStateLayer :type="stateLayerColor" :disabled="disabled" :force-pressed="forcePressed" />
    <BaseIcon v-if="icon" :size="iconSize" :class="leadingIconPadding" :filled="selected"
      >{{ icon }}
    </BaseIcon>
    <span class=""> <slot /> </span>
    <BaseIcon v-if="trailingIcon" :size="iconSize" :class="trailingIconPadding"
      >{{ trailingIcon }}
    </BaseIcon>
  </button>
</template>

<script lang="ts" setup>
  import { computed, useTemplateRef } from 'vue'
  import BaseIcon from '../../icon/BaseIcon.vue'
  import BaseStateLayer from '../../state-layer/BaseStateLayer.vue'
  import { useMousePressed } from '@vueuse/core'
  import type { TextButtonProps } from '../button-props'
  import { useButtonSize } from '@/core/composables/use-button-size'

  const {
    size = 'S',
    shape = 'round',
    color = 'text',
    disabled,
    selected,
    toggle,
    forcePressed = false,
  } = defineProps<TextButtonProps>()

  const { actualSize } = useButtonSize(size)

  const stateLayerColor = computed(() => {
    if (color == 'filled' && toggle && !selected) {
      return 'surface'
    }
    if (color == 'textOnSurface') {
      return 'surface-neutral'
    }
    return color
  })

  const buttonHeight = computed(() => {
    if (actualSize.value == 'XS') {
      return 'h-8'
    }
    if (actualSize.value == 'S') {
      return 'h-10'
    }
    if (actualSize.value == 'M') {
      return 'h-14'
    }
    if (actualSize.value == 'L') {
      return 'h-24'
    }
    return 'h-[136px]'
  })

  const label = computed(() => {
    if (actualSize.value == 'XS') {
      return 'text-label-large'
    }
    if (actualSize.value == 'S') {
      return 'text-label-large'
    }
    if (actualSize.value == 'M') {
      return 'text-title-medium'
    }
    if (actualSize.value == 'L') {
      return 'text-headline-small'
    }
    return 'text-headline-large'
  })

  const padding = computed(() => {
    if (actualSize.value == 'XS') {
      return 'px-3'
    }
    if (actualSize.value == 'S') {
      return 'px-4'
    }
    if (actualSize.value == 'M') {
      return 'px-6'
    }
    if (actualSize.value == 'L') {
      return 'px-12'
    }
    return 'px-16'
  })

  const leadingIconPadding = computed(() => {
    if (actualSize.value == 'XS') {
      return 'pr-1'
    }
    if (actualSize.value == 'S') {
      return 'pr-2'
    }
    if (actualSize.value == 'M') {
      return 'pr-2'
    }
    if (actualSize.value == 'L') {
      return 'pr-3'
    }
    return 'pr-4'
  })

  const trailingIconPadding = computed(() => {
    if (actualSize.value == 'XS') {
      return 'pl-1'
    }
    if (actualSize.value == 'S') {
      return 'pl-2'
    }
    if (actualSize.value == 'M') {
      return 'pl-2'
    }
    if (actualSize.value == 'L') {
      return 'pl-3'
    }
    return 'pl-4'
  })

  const iconSize = computed(() => {
    if (actualSize.value == 'XS') {
      return '20'
    }
    if (actualSize.value == 'S') {
      return '20'
    }
    if (actualSize.value == 'M') {
      return '24'
    }
    if (actualSize.value == 'L') {
      return '32'
    }
    return '40'
  })

  const button = useTemplateRef<HTMLButtonElement>('button')
  const { pressed } = useMousePressed({ target: button })

  const borderRadius = computed(() => {
    if (pressed.value || forcePressed) {
      if (actualSize.value == 'XS') {
        return 'rounded-[8px]'
      }
      if (actualSize.value == 'S') {
        return 'rounded-[8px]'
      }
      if (actualSize.value == 'M') {
        return 'rounded-[12px]'
      }
      if (actualSize.value == 'L') {
        return 'rounded-[16px]'
      }
      return 'rounded-[16px]'
    }

    if ((shape == 'round' && !selected) || (shape == 'square' && selected)) {
      if (actualSize.value == 'XS') {
        return 'rounded-[16px]'
      }
      if (actualSize.value == 'S') {
        return 'rounded-[20px]'
      }
      if (actualSize.value == 'M') {
        return 'rounded-[28px]'
      }
      if (actualSize.value == 'L') {
        return 'rounded-[48px]'
      }
      return 'rounded-[68px]'
    }

    if ((shape == 'round' && selected) || shape == 'square') {
      if (actualSize.value == 'XS') {
        return 'rounded-[12px]'
      }
      if (actualSize.value == 'S') {
        return 'rounded-[12px]'
      }
      if (actualSize.value == 'M') {
        return 'rounded-[16px]'
      }
      if (actualSize.value == 'L') {
        return 'rounded-[28px]'
      }
      return 'rounded-[28px]'
    }

    if (actualSize.value == 'XS') {
      return 'rounded-[16px]'
    }
    if (actualSize.value == 'S') {
      return 'rounded-[20px]'
    }
    if (actualSize.value == 'M') {
      return 'rounded-[28px]'
    }
    if (actualSize.value == 'L') {
      return 'rounded-[48px]'
    }
    return 'rounded-[68px]'
  })

  const colorClasses = computed(() => {
    if (color == 'elevated') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (toggle && selected) {
          return 'bg-primary text-on-primary shadow-1  hover:shadow-2'
        } else {
          return 'bg-surface-container-low text-primary shadow-1  hover:shadow-2'
        }
      }
    }
    if (color == 'filled') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (!toggle || selected) {
          return 'bg-primary text-on-primary hover:shadow-1'
        } else {
          return 'bg-surface-container text-on-surface-variant'
        }
      }
    }
    if (color == 'filledInverse') {
      if (disabled) {
        return 'bg-on-primary/[10%] text-on-primary/[38%]'
      } else {
        if (!toggle || selected) {
          return 'bg-on-primary text-primary hover:shadow-1'
        } else {
          return 'bg-primary-container text-on-primary-container'
        }
      }
    }
    if (color == 'filledTertiary') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (!toggle || selected) {
          return 'bg-tertiary text-on-tertiary hover:shadow-1'
        } else {
          return 'bg-surface-container text-on-surface-variant'
        }
      }
    }
    if (color == 'tonal') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'bg-secondary text-on-secondary hover:shadow-1'
        } else {
          return 'bg-secondary-container text-on-secondary-container hover:shadow-1'
        }
      }
    }
    if (color == 'tonalPrimary') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'bg-primary text-on-primary hover:shadow-1'
        } else {
          return 'bg-primary-container text-on-primary-container hover:shadow-1'
        }
      }
    }
    if (color == 'tonalTertiary') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'bg-tertiary text-on-tertiary hover:shadow-1'
        } else {
          return 'bg-tertiary-container text-on-tertiary-container hover:shadow-1'
        }
      }
    }
    if (color == 'outlined') {
      if (disabled) {
        return 'border border-outline-variant bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        if (selected) {
          return 'border bg-inverse-surface text-inverse-on-surface'
        } else {
          return 'border border-outline-variant text-on-surface-variant'
        }
      }
    }
    if (color == 'textOnSurface') {
      if (disabled) {
        return 'bg-on-surface/[10%] text-on-surface/[38%]'
      } else {
        return 'text-on-surface-variant'
      }
    }
    if (disabled) {
      return 'bg-on-surface/[10%] text-on-surface/[38%]'
    } else {
      return 'text-primary'
    }
  })
</script>
